import { useMemo, useRef, useCallback } from 'react';
import Fuse from 'fuse.js';
import { MapLedgerEventView, MapLedgerUnitView } from '@mark43/cad-api';
import { UNITS_SEARCH_KEYS, EVENTS_SEARCH_KEYS } from '../constants';

class EventSearch {
    fuse: Fuse<MapLedgerEventView>;

    constructor(searchItems: MapLedgerEventView[]) {
        this.fuse = new Fuse(searchItems, {
            keys: EVENTS_SEARCH_KEYS,
            threshold: 0.1,
            ignoreLocation: true,
        });
    }

    search = (searchText: string): MapLedgerEventView[] => {
        return this.fuse.search(searchText).map((result) => result.item);
    };
}

class UnitSearch {
    fuse: Fuse<MapLedgerUnitView>;

    constructor(searchItems: MapLedgerUnitView[]) {
        this.fuse = new Fuse(searchItems, {
            keys: UNITS_SEARCH_KEYS,
            threshold: 0.1,
            ignoreLocation: true,
        });
    }

    search = (searchText: string): MapLedgerUnitView[] => {
        return this.fuse.search(searchText).map((result) => result.item);
    };
}

class UserSearch {
    fuse: Fuse<MapLedgerUnitView>;

    constructor(searchItems: MapLedgerUnitView[]) {
        this.fuse = new Fuse(searchItems, {
            keys: UNITS_SEARCH_KEYS,
            threshold: 0.1,
            ignoreLocation: true,
        });
    }

    search = (searchText: string): MapLedgerUnitView[] => {
        return this.fuse.search(searchText).map((result) => result.item);
    };
}

type SearchesByEntityType = {
    eventSearch: EventSearch;
    unitSearch: UnitSearch;
    userSearch: UserSearch;
};
type SearchResultItems = {
    mapLedgerEvents: MapLedgerEventView[];
    mapLedgerUnits: MapLedgerUnitView[];
    mapLedgerUnitUsers: MapLedgerUnitView[];
};

export const useEsriMapEventAndUnitSearch = () => {
    const searchesByEntityType = useRef<SearchesByEntityType>();

    const hasSearchInitialized = () => {
        return searchesByEntityType.current !== undefined;
    };

    const initSearches = useMemo(
        () => ({ mapLedgerEvents, mapLedgerUnits, mapLedgerUnitUsers }: SearchResultItems) => {
            searchesByEntityType.current = {
                eventSearch: new EventSearch(mapLedgerEvents),
                unitSearch: new UnitSearch(mapLedgerUnits),
                userSearch: new UserSearch(mapLedgerUnitUsers),
            };
        },
        []
    );

    const initEventsAndUnits = useCallback(
        ({ mapLedgerEvents, mapLedgerUnits, mapLedgerUnitUsers }) => {
            initSearches({ mapLedgerEvents, mapLedgerUnits, mapLedgerUnitUsers });
        },
        [initSearches]
    );

    const queryEventsAndUnits = (searchText: string): SearchResultItems | undefined => {
        if (searchesByEntityType.current === undefined) {
            return;
        }
        const { eventSearch, unitSearch, userSearch } = searchesByEntityType.current;
        const searchResult = {
            mapLedgerEvents: eventSearch.search(searchText),
            mapLedgerUnits: unitSearch.search(searchText),
            mapLedgerUnitUsers: userSearch.search(searchText),
        };
        return searchResult;
    };

    const clearSearchInitialization = () => {
        searchesByEntityType.current = undefined;
    };

    return {
        hasSearchInitialized,
        initEventsAndUnits,
        queryEventsAndUnits,
        clearSearchInitialization,
    };
};
